import { accountNamespacePrefix, namespaceKey, } from '#pplx/frontend/browser-storage/activeAccount.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
/**
 * One-time per-account localStorage migration for multi-account web.
 *
 * Per-account isolation now lives in `SafeLocalStorage` itself (account-scoped by default,
 * `{ global: true }` to opt out), so there is no separate "account storage" wrapper to
 * discover — callers keep using the storage utility they already know. The active-account
 * identity helpers live in `activeAccount.ts` (import them from there directly). This module
 * owns only the migration that seeds an account's namespace from the existing global keys.
 */
const MIGRATION_SENTINEL = '__migrated';
/**
 * Seeds an account's namespace by copying the existing un-namespaced (global) values for
 * the given keys (and any key matching one of `prefixes`) into `pplx:account:{accountId}:`.
 * Runs at most once per account, guarded by a sentinel key.
 *
 * Copy, not move: leaving the originals intact keeps the feature reversible — disabling
 * multi-account falls back to reading the original keys with no data loss. Every access
 * uses `{ global: true }` because the sources are global keys and the destinations are
 * already-namespaced literal keys, so neither should be re-scoped by `SafeLocalStorage`.
 *
 * @returns true if migration ran, false if it was already done for this account.
 */
export function migrateKeysToAccount({ accountId, keys = [], prefixes = [], }) {
    const guardKey = namespaceKey(MIGRATION_SENTINEL, accountId);
    if (SafeLocalStorage.getItem(guardKey, { global: true }) !== null) {
        return false;
    }
    const prefixMatches = prefixes.length
        ? SafeLocalStorage.keys().filter((key) => prefixes.some((prefix) => key.startsWith(prefix)))
        : [];
    for (const key of new Set([...keys, ...prefixMatches])) {
        const existing = SafeLocalStorage.getItem(key, { global: true });
        if (existing !== null) {
            SafeLocalStorage.setItem(namespaceKey(key, accountId), existing, { global: true });
        }
    }
    SafeLocalStorage.setItem(guardKey, '1', { global: true });
    return true;
}
/**
 * Removes every localStorage key in an account's namespace (`pplx:account:{accountId}:…`),
 * leaving other accounts and global keys intact. Used when signing out a single account so its
 * local data doesn't linger. `{ global: true }` on each remove because the keys are already
 * namespaced literals and must not be re-scoped.
 */
export function clearAccountStorage(accountId) {
    const prefix = accountNamespacePrefix(accountId);
    for (const key of SafeLocalStorage.keys()) {
        if (key.startsWith(prefix)) {
            SafeLocalStorage.removeItem(key, { global: true });
        }
    }
}
//# sourceMappingURL=accountStorage.js.map